


import { Component, Vue } from "vue-property-decorator";

import { CustomError, CustomNotification } from "@Internal/Models";

import { Newsletters } from "App";
import { $t, EventBus } from "App/IOC";
import { Newsletter } from "App/Models/Newsletter";

import BooleanField from "Web/Components/Common/Input/Boolean.vue";
import Icon from "Web/Components/Icon.vue";
import Popup from "Web/Components/Common/Popup.vue";

import NewsletterForm from "./Form.vue";
import NewsletterGroupBlogs from "./GroupBlogs.vue";
import NewsletterTest from "./Test.vue";
import { DescribeFrequency, ErrorMessage, FormatDate } from "./Helpers";

/* LA NEWSLETTER AUTOMATIQUE D'UN CLIENT ( Fred, 19-21/10/2026 ).

   Tout se fait d'ici, sans aller sur le compte du client : activer l'offre, creer
   et regler ses newsletters, essai, apercu, statistiques. Seule exception :
   l'e-mail, construit dans le PB e-mail du client ( son Desktop ). Une newsletter
   sans bloc rempli a l'envoi ne peut pas etre allumee ( `has_dynamic_block` ).

   Les deux cotes ecrivent la meme ressource : ce que le client change se voit
   ici a la relecture, et l'inverse ( `updated_by` ). */
@Component({
    components: {
        NewsletterGroupBlogs,
        BooleanField,
        Icon,
        NewsletterForm,
        NewsletterTest,
        Popup
    }
})
export default class NewslettersGroup extends Vue {
    private loading: boolean = true;
    private enabled: boolean = false;
    private switching: boolean = false;
    private newsletters: Newsletter[] = [];

    /* Le back de la Console n'a pas encore le relais ( `Internal/Newsletters`, a
       deployer ) : rien ne peut se lire ni s'ecrire, l'ecran le dit. */
    private relayMissing: boolean = false;

    private editing: Newsletter | null = null;
    private creating: boolean = false;
    private testing: Newsletter | null = null;
    /* Ou poser l'essai quand il s'ouvre depuis le reglage ( sous son bouton ). */
    private testAnchor: { top: number; right: number } | null = null;

    /* L'onglet ouvert : les newsletters d'abord. */
    private tab: "newsletters" | "blogs" = "newsletters";
    private readonly tabs: ( "newsletters" | "blogs" )[] = [ "newsletters", "blogs" ];

    private get groupId(): number {
        return Number( this.$route.params.groupId );
    }

    private get title(): string {
        const query = this.$route.query;
        const name = String( query.company || query.name || "" );

        return ( name !== "" ) ? name : $t( "NEWSLETTERS_group", { id: this.groupId } ).toString();
    }

    private get subtitle(): string {
        const query = this.$route.query;

        return [ query.company ? query.name : "", query.email, `#${ this.groupId }` ]
            .filter( part => !!part ).join( " — " );
    }

    async mounted(): Promise<void> {
        await this.refresh();
    }

    private tabLabel( item: string ): string {
        return $t( item === "blogs" ? "NEWSLETTERS_tabBlogs" : "NEWSLETTERS_tabNewsletters" );
    }

    private async refresh(): Promise<void> {
        this.loading = true;

        const [ [ enabled, enabledError ], [ list, listError ] ] = await Promise.all( [
            Newsletters.Enabled( this.groupId ),
            Newsletters.List( this.groupId )
        ] );

        this.loading = false;

        const error = enabledError || listError;

        if( error !== undefined && String( error.Message ) === "Page not found" ) {
            this.relayMissing = true;
            return;
        }

        if( error !== undefined ) {
            this.fail( error );
            return;
        }

        this.relayMissing = false;

        this.enabled = enabled === true;
        this.newsletters = list || [];
    }

    private fail( error: CustomError | undefined ): void {
        if( error === undefined )
            return;

        EventBus.Publish(
            "Error", new CustomError( error.Id, ErrorMessage( error.Message, error.SubId ) )
        );
    }

    private notify( message: string ): void {
        EventBus.Publish( "Notification", CustomNotification.Success(
            $t( "NEWSLETTERS_title" ), message
        ) );
    }

    /* Eteindre l'offre arrete les envois ; le parametrage reste. */
    private async toggleOffer( value: boolean ): Promise<void> {
        this.switching = true;

        const [ enabled, error ] = await Newsletters.SetEnabled( this.groupId, value );

        this.switching = false;

        if( error !== undefined ) {
            this.fail( error );
            return;
        }

        this.enabled = enabled === true;
        this.notify(
            $t( this.enabled ? "NEWSLETTERS_offerOn" : "NEWSLETTERS_offerOff" ).toString()
        );
    }

    private async toggleActive( newsletter: Newsletter, value: boolean ): Promise<void> {
        const [ updated, error ] = await Newsletters.Update( this.groupId, newsletter.id, {
            active: value
        } );

        if( error !== undefined || updated === undefined ) {
            this.fail( error );
            await this.refresh();
            return;
        }

        this.replace( updated );
    }

    /* L'essai ouvert depuis le bouton d'en-tete du reglage, 8 px sous lui. */
    private openTestFrom( button: DOMRect ): void {
        this.testAnchor = {
            top: Math.round( button.bottom + 8 ),
            right: Math.round( window.innerWidth - button.right )
        };
        this.testing = this.editing;
    }

    private replace( updated: Newsletter ): void {
        const index = this.newsletters.findIndex( item => item.id === updated.id );

        if( index >= 0 )
            this.newsletters.splice( index, 1, updated );
        else
            this.newsletters.push( updated );
    }

    private confirmDelete( newsletter: Newsletter ): void {
        EventBus.Publish( "Confirmation", {
            Props: {
                Message: $t( "NEWSLETTERS_deleteConfirm", { name: newsletter.name } ),
                Actions: [ {
                    Id: "delete",
                    Name: $t( "NEWSLETTERS_delete" ),
                    Active: true
                } ]
            },
            Events: { Action: () => this.remove( newsletter ) }
        } );
    }

    private async remove( newsletter: Newsletter ): Promise<void> {
        const error = await Newsletters.Delete( this.groupId, newsletter.id );

        if( error !== undefined ) {
            this.fail( error );
            return;
        }

        this.newsletters = this.newsletters.filter( item => item.id !== newsletter.id );
        this.notify( $t( "NEWSLETTERS_deleted" ).toString() );
    }

    /* Les statistiques ont leur ecran ( Fred, 22/10/2026 ) ; l'adresse garde le nom
       et l'e-mail du client pour le retour. */
    private openStats( newsletter: Newsletter ): void {
        this.$router.push( {
            name: "newsletters-stats",
            params: { groupId: String( this.groupId ), id: String( newsletter.id ) },
            query: this.$route.query
        } );
    }

    private onSaved( newsletter: Newsletter ): void {
        this.replace( newsletter );
        this.creating = false;
        this.editing = null;
        this.notify( $t( "NEWSLETTERS_saved" ).toString() );
    }

    private author( newsletter: Newsletter ): string {
        if( newsletter.updated_at === null )
            return "";

        /* Qui a modifie, sous la date de la colonne « Modifiee le / par ». */
        const who = newsletter.updated_by !== undefined && newsletter.updated_by.console
            ? $t( "NEWSLETTERS_byConsole" ).toString()
            : $t( "NEWSLETTERS_byCustomer" ).toString();

        return who.charAt( 0 ).toUpperCase() + who.slice( 1 );
    }

    /* Ce qui empeche d'allumer la newsletter ( ou de la faire partir ), ou rien. */
    private warning( newsletter: Newsletter ): string {
        const messages: string[] = [];

        if( !newsletter.has_dynamic_block )
            messages.push( $t( "NEWSLETTERS_noDynamicBlock" ).toString() );
        else if( !newsletter.sender_email )
            messages.push( $t( "NEWSLETTERS_noSenderWarning" ).toString() );

        if( !this.enabled && newsletter.active )
            messages.push( $t( "NEWSLETTERS_offerOffWarning" ).toString() );

        return messages.join( "\n" );
    }

    private frequency( newsletter: Newsletter ): string {
        return DescribeFrequency( newsletter.frequency );
    }

    private date( value: string | null ): string {
        return FormatDate( value );
    }
}
